import React from 'react';

import {
    addConsultant,
    ConsultantFetchAction,
    fetchAssignedConsultans,
    removeConsultant,
    toggleDefaultConsultant,
} from '../Api/AssignedConsultants';
import { readCart, saveCart } from '../Api/ShopwareCart';
import { AssignedConsultant } from '../MyLom/Account/ConsultantAssignment/Types';
import { Account } from '../MyLom/Account/EditAccount/Types';
import { AccountContext } from './AccountContext';

type ContextFunction = (
    consultantId: string,
    onSuccess?: (consultants: AssignedConsultant[]) => void,
    onError?: (json: any) => void,
) => void;

interface AssignedConsultantContext {
    assignedConsultants: AssignedConsultant[];
    updateAssignedConsultants: React.Dispatch<React.SetStateAction<AssignedConsultant[]>>;
    assignConsultant: ContextFunction;
    dischargeConsultant: ContextFunction;
    toggleDefault: ContextFunction;
    loadingConsultants: boolean;
}

export const AssignedConsultantContext = React.createContext<AssignedConsultantContext>({
    assignedConsultants: [],
    updateAssignedConsultants: () => {},
    assignConsultant: () => {},
    dischargeConsultant: () => {},
    toggleDefault: () => {},
    loadingConsultants: true,
});

function makeContextFunction(
    fn: ConsultantFetchAction,
    updateAssignedConsultants: React.Dispatch<React.SetStateAction<AssignedConsultant[]>>,
    accountData: Account,
): ContextFunction {
    return (
        consultantId: string,
        onSuccess?: (consultants: AssignedConsultant[]) => void,
        onError?: (json: any) => void,
    ) => {
        fn(
            consultantId,
            (newConsultants) => {
                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(newConsultants);
                }
                // also update current default constultant in cart for every action
                updateDefaultConsultantInCart(newConsultants, accountData);
                updateAssignedConsultants(newConsultants);
            },
            (error) => {
                if (onError && typeof onError === 'function') {
                    onError(error);
                }
                throw new Error('Error while updating consultants');
            },
        );
    };
}

export const AssignedConsultantsValueProvider: React.FunctionComponent = (props) => {
    const [assignedConsultants, updateAssignedConsultants] = React.useState<AssignedConsultant[]>([]);
    const [loadingConsultants, updateLoadingConsultants] = React.useState(true);
    const { accountData, isLoggedIn } = React.useContext(AccountContext);

    if (!isLoggedIn || !accountData) {
        throw new Error('can not render component without account data');
    }

    const assignConsultant = makeContextFunction(addConsultant, updateAssignedConsultants, accountData);
    const dischargeConsultant = makeContextFunction(removeConsultant, updateAssignedConsultants, accountData);
    const toggleDefault = makeContextFunction(toggleDefaultConsultant, updateAssignedConsultants, accountData);

    const readInitiallyAssignedConsultants = React.useCallback(
        () =>
            fetchAssignedConsultans(
                (newAssignedConsultants) => {
                    updateLoadingConsultants(false);
                    updateAssignedConsultants(newAssignedConsultants);
                },
                () => {
                    updateLoadingConsultants(false);
                },
            ),
        [],
    );

    React.useEffect(() => readInitiallyAssignedConsultants(), []);

    return (
        <AssignedConsultantContext.Provider
            value={{
                assignedConsultants,
                updateAssignedConsultants,
                assignConsultant,
                dischargeConsultant,
                toggleDefault,
                loadingConsultants,
            }}
        >
            {props.children}
        </AssignedConsultantContext.Provider>
    );
};

/**
 * We also need to update the new consulant id within the server-side cart. Since we can not use
 * the cart context here (the cart context is not a parent of this context), we go the long
 * way and retrieve the current cart from the server and send it back including the updated consultant
 * id. All this is happening in the background, so the enduser does not have to wait until this request marathon has finished.
 *
 * @param newConsultants
 * @param accountData
 */
function updateDefaultConsultantInCart(newConsultants: AssignedConsultant[], accountData: Account) {
    const defaultConsultant = newConsultants.find((consultant) => consultant.default);

    readCart(
        accountData,
        (customerCarts) =>
            saveCart(
                accountData.id,
                { mode: 'shopware', carts: customerCarts, visible: false },
                defaultConsultant ? defaultConsultant.id : null,
                () => {
                    // console.log('evertyhing went fine');
                },
                (error) => {
                    throw new Error('Could not update cart after changing default consultant');
                },
            ),
        (error) => {
            throw new Error('Could not update cart after changing default consultant');
        },
    );
}
